/* eslint-disable @next/next/no-img-element */
/* eslint-disable @typescript-eslint/no-explicit-any */

"use client";

import { mergeAttributes, Node } from "@tiptap/core";
import { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { ExpandIcon, XIcon } from "lucide-react";
import React, { useCallback } from "react";

interface LinkMetadata {
	title?: string;
	description?: string;
	image?: string;
	url: string;
}

interface SmartLinkOptions {
	HTMLAttributes: Record<string, any>;
	openInNewTab: boolean;
	displayAs?: "card" | "link";
	onMetadataFetch?: (url: string) => Promise<LinkMetadata>;
}

const LinkPreviewCard: React.FC<{
	metadata: LinkMetadata;
	onClose?: () => void;
	onExpand?: () => void;
	displayAs: "card" | "link";
}> = ({ metadata, onClose, onExpand, displayAs }) => {
	if (!metadata) return null;

	if (displayAs === "link") {
		return (
			<span className="inline-flex items-center gap-2">
				<a
					href={metadata.url}
					className="text-blue-600 underline hover:text-blue-800"
					target="_blank"
					rel="noopener noreferrer"
				>
					{metadata.url}
				</a>

				<button
					onClick={onExpand}
					className="inline-flex items-center justify-center rounded-full p-1 hover:bg-gray-100"
					title="Show preview"
				>
					<ExpandIcon className="h-4 w-4" />
				</button>
			</span>
		);
	}

	return (
		<div className="my-2 max-w-2xl overflow-hidden rounded-lg border border-gray-200">
			<div className="relative">
				{onClose && (
					<button
						onClick={onClose}
						className="absolute right-2 top-2 rounded-full bg-white p-1 shadow-md hover:bg-gray-100"
					>
						<XIcon className="h-4 w-4" />
					</button>
				)}

				{metadata.image && (
					<div className="h-48 w-auto overflow-hidden object-contain">
						<img
							src={metadata.image}
							alt={metadata.title || "Link preview"}
							className="h-full w-full object-cover"
						/>
					</div>
				)}

				<div className="p-4">
					<h3 className="mb-2 text-lg font-medium">{metadata.title}</h3>

					{metadata.description && (
						<p className="line-clamp-2 text-sm text-gray-600">{metadata.description}</p>
					)}

					<div className="mt-2 truncate text-sm text-gray-500">{metadata.url}</div>
				</div>
			</div>
		</div>
	);
};

const SmartLinkComponent: React.FC<{
	node: ProseMirrorNode;
	updateAttributes: (attrs: Record<string, any>) => void;
}> = ({ node, updateAttributes }) => {
	const onClose = useCallback(() => {
		updateAttributes({
			...node.attrs,
			displayAs: "link",
		});
	}, [updateAttributes, node.attrs]);

	const onExpand = useCallback(() => {
		updateAttributes({
			...node.attrs,
			displayAs: "card",
		});
	}, [updateAttributes, node.attrs]);

	return (
		<NodeViewWrapper>
			<LinkPreviewCard
				onClose={onClose}
				onExpand={onExpand}
				metadata={node.attrs.metadata}
				displayAs={node.attrs.displayAs}
			/>
		</NodeViewWrapper>
	);
};

const URL_REGEX =
	// eslint-disable-next-line no-useless-escape
	/https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)/;

const defaultMetadataFetcher = async (url: string): Promise<LinkMetadata> => {
	const response = await fetch(`/api/metadata?url=${encodeURIComponent(url)}`);
	const { data } = await response.json();

	return data;
};

export const SmartLink = Node.create<SmartLinkOptions>({
	name: "smartLink",

	addOptions() {
		return {
			HTMLAttributes: {},
			openInNewTab: true,
			displayAs: "card",
			onMetadataFetch: defaultMetadataFetcher,
		};
	},

	group: "inline",

	inline: true,

	atom: true,

	addAttributes() {
		return {
			href: {
				default: null,
			},
			displayAs: {
				default: this.options.displayAs,
			},
			metadata: {
				default: null,
				rendered: true,
			},
		};
	},

	parseHTML() {
		return [
			{
				tag: "a[data-smart-link]",
				getAttrs: (element) => {
					if (!(element instanceof HTMLElement)) {
						return false;
					}

					return {
						href: element.getAttribute("href"),
						displayAs: element.getAttribute("data-display-as"),
						metadata: JSON.parse(element.getAttribute("data-metadata") || "{}"),
					};
				},
			},
		];
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"a",
			mergeAttributes({ "data-smart-link": "" }, this.options.HTMLAttributes, HTMLAttributes),
		];
	},

	addNodeView() {
		return ReactNodeViewRenderer(SmartLinkComponent);
	},

	addProseMirrorPlugins() {
		const plugin = new Plugin({
			key: new PluginKey("smartLink"),
			props: {
				handlePaste: (view, event) => {
					const text = event.clipboardData?.getData("text/plain");
					if (!text) return false;

					const matches = text.match(URL_REGEX);
					if (!matches) return false;

					const url = matches[0];
					const { tr } = view.state;
					const otherParts = text.split(url);
					// Insert the text that comes before the URL
					if (otherParts[0]) {
						tr.insertText(otherParts[0], tr.selection.from);
					}

					const node = this.type.create({
						href: url,
						displayAs: this.options.displayAs,
						metadata: { url },
					});

					tr.replaceSelectionWith(node);
					if (otherParts[1]) {
						tr.insertText(otherParts[1], tr.selection.from);
					}

					view.dispatch(tr);

					this.options.onMetadataFetch?.(url).then((metadata) => {
						const { doc } = view.state;
						let nodePos = -1;

						doc.descendants((node, pos) => {
							if (node.type.name === this.name && node.attrs.href === url) {
								nodePos = pos;
								return false;
							}
						});

						if (nodePos > -1) {
							const newTr = view.state.tr.setNodeAttribute(nodePos, "metadata", {
								...metadata,
								url,
							});

							view.dispatch(newTr);
						}
					});

					return true;
				},
			},
		});

		return [plugin];
	},
});
